import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchCurrencies } from '../../hooks/useFetchCurrencies';
import { ICurrency } from '../../interface/currencies';
import { IMatterRequest } from '../../interface/matterRequest';

/**
 * The props `SearchableSelect` needs to pick a matter request's currency, from the same list the
 * picker screen uses. Only the code is stored on the request, so the chosen value shows the code.
 */
export const useMatterRequestCurrencySelect = () => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { language: appLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: currencies,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchCurrencies({ pageSize: 100, search: searchValue });

  const options: SelectOption[] = currencies
    .map((currency: ICurrency) => ({
      value: currency.currencyCode,
      label: getTranslationFromTranslatableEntity(currency.translations, appLanguage),
    }))
    .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label, appLanguage));
  const value: SelectOption | null = matterRequest?.currency
    ? { value: matterRequest.currency, label: matterRequest.currency }
    : null;

  const onChange = (option: SelectOption) => {
    const currency = currencies.find(
      ({ currencyCode }: ICurrency) => currencyCode === option.value,
    );
    if (!currency) return;
    updateManageMatterRequest({
      ...matterRequest,
      currency: currency.currencyCode,
      isCurrencySetByUser: true,
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
